import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  FaUsers,
  FaProjectDiagram,
  FaExclamationCircle,
  FaChartLine,
  FaHeadset,
  FaAddressBook,
  FaHome,
  FaLock,
  FaUserTie,
  FaWallet,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaChevronDown,
  FaChevronUp,
  FaBullhorn,
  FaFolderOpen,
  FaCogs,
  FaCoins,
  FaTasks,
  FaBook,
  FaCalendarAlt,
  FaUser,
  FaKey,
  FaCog,
  FaSignOutAlt,
  FaGlobe,
  FaClipboardCheck,
  FaInbox,
  FaUsersCog,
  FaPlug,
  FaSitemap,
  FaBriefcase,
  FaComments,
  FaListUl,
  FaWhatsapp,
  FaFacebookMessenger,
  FaFileInvoice,
  FaFileInvoiceDollar,
  FaHandHoldingUsd,
  FaSlidersH,
  FaDotCircle,
  FaMobileAlt,
  FaUserFriends,
  FaPercent,
  FaMoneyCheckAlt,
  FaExternalLinkAlt,
} from "react-icons/fa";
import "../assets/css/Sidebar.css";
import { ADMIN_FINANCE_PAGE_SECTIONS } from "../utils/financeTerms";
import NotificationBell from "../pages/work/NotificationBell";
import { staffWorkApi } from "../pages/work/workApi";
import { getCurrentUserRole, getAllowedSidebarPages, getStoredRolePermissions } from "../utils/roleAccess";
import { getSidebarTheme, sidebarThemeClassName } from "../utils/sidebarTheme";
import { getAuthItem } from "../utils/authStorage";
import { resolveImageUrl, getApiUrl } from "../config/apiConfig";
import { clearAuthSessionStorage } from "../utils/clearSessionStorage";
import { resolveBrand, TUMMA_LOGO_SRC } from "../utils/brand";

const SIDEBAR_GROUPS_STORAGE_KEY = "cloudOfficeSidebarGroupsOpen_v3";

const WORK_PAGES = ["MyWork", "WorkFollowUps", "ReviewInbox", "TeamBoard", "WorkPlan"];
/** Social / posting pages live under DMS and follow Digital Marketing access. */
const DM_SOCIAL_PAGES = ["SocialComments", "SocialMessages", "WhatsApp", "SocialConnections"];
/** Finance pages under FMS all follow the Finance Management permission. */
const FINANCE_PAGES = Object.keys(ADMIN_FINANCE_PAGE_SECTIONS);
/** Circles (Digix Leads agent network) pages all follow the Circles permission. */
const CIRCLES_PAGES = [
  "CirclesInbox",
  "CirclesAppRegistrations",
  "CirclesAgents",
  "CirclesProjects",
  "CirclesCommissions",
  "CirclesPayouts",
  "CirclesSettings",
  "CirclesDashboard",
];

const PAGE_HINTS = {
  ...Object.fromEntries(Object.entries(ADMIN_FINANCE_PAGE_SECTIONS).map(([id, s]) => [id, `${s.label}: ${s.hint}`])),
  CirclesInbox: "Digix Leads inbox: convert Circles enquiries and sourced providers into client leads",
  CirclesAppRegistrations: "Check and confirm customer app registrations made with a referral code",
  CirclesDashboard: "Open the Digix staff dashboard (Agent Search, Leads Sourcing), signed in",
};

const PAGE_LABELS = {
  Dashboard: "Dashboard",
  MyWork: "My Work",
  WorkFollowUps: "Follow-ups",
  ReviewInbox: "Review Inbox",
  TeamBoard: "Team Board",
  SocialComments: "Comments",
  SocialMessages: "Messages",
  WhatsApp: "WhatsApp",
  SocialConnections: "API Connections",
  WorkPlan: "Plan & Assign",
  Users: "Employees",
  OrgChart: "Org Chart",
  Projects: "Projects",
  UserStories: "User Stories",
  Tasks: "Tasks",
  Issues: "Issue Tracker",
  DigixLeads: "Leads",
  WebsiteLeads: "Website Leads",
  CirclesInbox: "Inbox",
  CirclesAppRegistrations: "App Registrations",
  CirclesAgents: "Agents",
  CirclesProjects: "Projects",
  CirclesCommissions: "Commissions",
  CirclesPayouts: "Payouts",
  CirclesSettings: "Settings",
  CirclesDashboard: "Staff Dashboard",
  DMProjects: "Marketing Profiles",
  DigitalMarketing: "Calendar",
  Tenants: "Clients",
  Accounts: "Vault",
  HRManagement: "Ai Hire",
  ...Object.fromEntries(Object.entries(ADMIN_FINANCE_PAGE_SECTIONS).map(([id, s]) => [id, s.label])),
  "Support Contacts": "Support",
};

const PAGE_ICONS = {
  Dashboard: FaHome,
  MyWork: FaClipboardCheck,
  WorkFollowUps: FaListUl,
  ReviewInbox: FaInbox,
  TeamBoard: FaUsersCog,
  SocialComments: FaComments,
  SocialMessages: FaFacebookMessenger,
  WhatsApp: FaWhatsapp,
  SocialConnections: FaPlug,
  WorkPlan: FaSitemap,
  Users: FaUsers,
  OrgChart: FaSitemap,
  Projects: FaProjectDiagram,
  UserStories: FaBook,
  Tasks: FaTasks,
  Issues: FaExclamationCircle,
  DigixLeads: FaChartLine,
  WebsiteLeads: FaGlobe,
  CirclesInbox: FaInbox,
  CirclesAppRegistrations: FaMobileAlt,
  CirclesAgents: FaUserFriends,
  CirclesProjects: FaProjectDiagram,
  CirclesCommissions: FaPercent,
  CirclesPayouts: FaMoneyCheckAlt,
  CirclesSettings: FaSlidersH,
  CirclesDashboard: FaExternalLinkAlt,
  DMProjects: FaFolderOpen,
  DigitalMarketing: FaCalendarAlt,
  Tenants: FaAddressBook,
  Accounts: FaLock,
  HRManagement: FaUserTie,
  FinanceInvoices: FaFileInvoice,
  FinanceReceivables: FaFileInvoiceDollar,
  FinancePaymentsReceived: FaHandHoldingUsd,
  FinancePayables: FaWallet,
  FinanceSettings: FaSlidersH,
  "Support Contacts": FaHeadset,
};

const SIDEBAR_STRUCTURE = [
  { type: "item", id: "Dashboard" },
  {
    type: "group",
    id: "work",
    label: "Work",
    icon: FaBriefcase,
    items: WORK_PAGES,
  },
  { type: "item", id: "Tenants" },
  {
    type: "group",
    id: "pms",
    label: "PMS",
    icon: FaCogs,
    items: ["Projects", "UserStories", "Tasks", "Issues"],
  },
  {
    type: "group",
    id: "dm",
    label: "DMS",
    icon: FaBullhorn,
    items: [
      "DMProjects",
      "SocialConnections",
      "DigitalMarketing",
      "SocialComments",
      "SocialMessages",
      "WhatsApp",
      "DigixLeads",
      "WebsiteLeads",
    ],
  },
  {
    type: "group",
    id: "circles",
    label: "Circles",
    icon: FaDotCircle,
    items: CIRCLES_PAGES,
  },
  {
    type: "group",
    id: "hrms",
    label: "HRMS",
    icon: FaUserTie,
    items: ["Users", "OrgChart", "HRManagement"],
  },
  {
    type: "group",
    id: "fms",
    label: "FMS",
    icon: FaCoins,
    items: FINANCE_PAGES,
  },
  { type: "divider", id: "after-fms" },
  { type: "item", id: "Accounts" },
  { type: "item", id: "Support Contacts" },
];

const DEFAULT_OPEN_GROUPS = {
  work: true,
  pms: true,
  dm: false,
  circles: false,
  hrms: false,
  fms: false,
};

function readOpenGroups() {
  try {
    const raw = localStorage.getItem(SIDEBAR_GROUPS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_OPEN_GROUPS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_OPEN_GROUPS, ...parsed };
  } catch {
    return { ...DEFAULT_OPEN_GROUPS };
  }
}

/** The Digix staff dashboard link shows for admins and anyone with a Circles dashboard permission. */
function canOpenStaffDashboard(role) {
  if (/admin/.test(String(role || ""))) return true;
  const stored = getStoredRolePermissions() || [];
  return stored.some((p) => ["Circles Agent Search", "Circles Leads Sourcing"].includes(p.Permission_Name || p.permission_name));
}

function findGroupIdForPage(pageId) {
  for (const entry of SIDEBAR_STRUCTURE) {
    if (entry.type === "group" && entry.items.includes(pageId)) return entry.id;
  }
  return null;
}

function readUser() {
  try {
    const userStr = getAuthItem("user");
    if (userStr) return JSON.parse(userStr);
  } catch {
    /* ignore */
  }
  return null;
}

function Sidebar({
  activePage,
  onNavigate,
  isOpen = false,
  collapsed = false,
  onToggleCollapse,
  onLogout,
  onMyDetails,
  onChangePassword,
  onPermissions,
  onSettings,
}) {
  const location = useLocation();
  const role = getCurrentUserRole();
  const allowedPages = getAllowedSidebarPages(role);
  const [theme, setTheme] = useState(() => getSidebarTheme("office"));
  const [openGroups, setOpenGroups] = useState(readOpenGroups);
  const [user, setUser] = useState(() => readUser());
  const brand = useMemo(() => resolveBrand(), []);
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const sync = (e) => {
      if (e?.detail?.scope === "office" && e.detail.theme) {
        setTheme(e.detail.theme);
        return;
      }
      setTheme(getSidebarTheme("office"));
    };
    window.addEventListener("sidebarThemeChanged", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("sidebarThemeChanged", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    const load = () => setUser(readUser());
    load();
    const onStorage = (e) => {
      if (e.key === "user" || e.key === null) load();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("userDataUpdated", load);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("userDataUpdated", load);
    };
  }, []);

  useEffect(() => {
    setImageError(false);
  }, [user?.user_id, user?.image_url, user?.image_path]);

  useEffect(() => {
    if (!profileMenuOpen) return undefined;
    const onDoc = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [profileMenuOpen]);

  const allowedSet = useMemo(() => {
    const s = new Set(allowedPages);
    if (s.has("Projects")) {
      s.add("UserStories");
      s.add("Tasks");
    }
    // My Work is for everyone; the other Work pages come from role permissions (allowedPages).
    if (s.has("Dashboard")) s.add("MyWork");
    if (s.has("Users")) s.add("OrgChart");
    if (s.has("DMProjects") || s.has("DigitalMarketing")) DM_SOCIAL_PAGES.forEach((p) => s.add(p));
    if (s.has("FinanceManagement")) FINANCE_PAGES.forEach((p) => s.add(p));
    if (s.has("Circles")) {
      CIRCLES_PAGES.forEach((p) => s.add(p));
      if (!canOpenStaffDashboard(role)) s.delete("CirclesDashboard");
    }
    return s;
  }, [allowedPages, role]);

  const structure = useMemo(() => {
    const mapped = SIDEBAR_STRUCTURE.map((entry) => {
      if (entry.type === "divider") return entry;
      if (entry.type === "item") {
        return allowedSet.has(entry.id) ? entry : null;
      }
      const items = entry.items.filter((id) => allowedSet.has(id));
      if (items.length === 0) return null;
      return { ...entry, items };
    }).filter(Boolean);

    return mapped.filter((entry, index) => {
      if (entry.type !== "divider") return true;
      const hasBefore = mapped.slice(0, index).some((e) => e.type !== "divider");
      const hasAfter = mapped.slice(index + 1).some((e) => e.type !== "divider");
      return hasBefore && hasAfter;
    });
  }, [allowedSet]);

  useEffect(() => {
    const groupId = findGroupIdForPage(activePage);
    if (!groupId) return;
    setOpenGroups((prev) => {
      if (prev[groupId]) return prev;
      const next = { ...prev, [groupId]: true };
      try {
        localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, [activePage]);

  const toggleGroup = (groupId) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [groupId]: !prev[groupId] };
      try {
        localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const isAdminUser = (() => {
    if (!user) return false;
    const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
    const roleName = (
      (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
      user.role_name ||
      user.role ||
      ""
    )
      .toString()
      .trim()
      .toLowerCase();
    return roleName === "admin";
  })();

  const userName = user
    ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || "User"
    : "User";
  const userEmail = user ? user.company_email || user.email || "" : "";
  const userInitial = user?.first_name ? user.first_name.charAt(0).toUpperCase() : "U";

  const userImageUrl = (() => {
    if (!user) return null;
    let imageUrl = null;
    if (user.image_url) {
      imageUrl = resolveImageUrl(user.image_url);
    } else if (user.image_path) {
      const API_HOST = getApiUrl();
      if (API_HOST) {
        imageUrl = `${API_HOST.replace(/\/$/, "")}/uploads/cloud-office/users/${user.image_path}`;
      }
    }
    if (!imageUrl) return null;
    const separator = imageUrl.includes("?") ? "&" : "?";
    return `${imageUrl}${separator}t=${user.updated_on || Date.now()}`;
  })();

  const handleSignOut = () => {
    clearAuthSessionStorage();
    sessionStorage.clear();
    if (onLogout) onLogout();
  };

  const runProfileAction = (fn) => {
    setProfileMenuOpen(false);
    if (typeof fn === "function") fn();
  };

  const renderNavItem = (itemId) => {
    const Icon = PAGE_ICONS[itemId];
    const label = PAGE_LABELS[itemId] || itemId;
    return (
      <div
        key={itemId}
        role="button"
        tabIndex={0}
        className={`nav-item ${activePage === itemId ? "active" : ""}`}
        onClick={() => onNavigate && onNavigate(itemId)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onNavigate && onNavigate(itemId);
          }
        }}
        title={PAGE_HINTS[itemId] || label}
      >
        {Icon && <Icon className="nav-item-icon" />}
        <span className="nav-item-label">{label}</span>
      </div>
    );
  };

  const alertsItem = (
    <NotificationBell
      key="alerts"
      api={staffWorkApi}
      variant="nav"
      className="nav-item sidebar-signout-item"
    />
  );
  const hasSupportItem = structure.some((entry) => entry.id === "Support Contacts");

  const profileMenuActive = ["/my-details", "/change-password", "/permissions", "/settings"].some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
  );

  return (
    <div
      className={`sidebar${isOpen ? " is-open" : ""}${collapsed ? " is-collapsed" : ""} ${sidebarThemeClassName(theme)}`}
    >
      <div className="sidebar-header">
        <div className="sidebar-brand" title={`${brand.officeName} OS`}>
          <img
            src={brand.logo && !brandLogoFailed ? brand.logo : TUMMA_LOGO_SRC}
            alt=""
            className="sidebar-brand-mark"
            onError={() => setBrandLogoFailed(true)}
          />
          <div className="sidebar-brand-text">
            <div className="sidebar-logo">
              <span className="sidebar-logo-office">{brand.companyWord} OFFICE</span>{" "}
              <span className="sidebar-logo-os">OS</span>
            </div>
          </div>
        </div>
        {onToggleCollapse && (
          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={onToggleCollapse}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <FaAngleDoubleRight size={14} /> : <FaAngleDoubleLeft size={14} />}
          </button>
        )}
      </div>

      <nav className="sidebar-nav">
        {structure.map((entry) => {
          if (entry.type === "divider") {
            return <div key={entry.id} className="sidebar-nav-divider" role="separator" />;
          }
          if (entry.type === "item") {
            if (entry.id === "Support Contacts") {
              return (
                <React.Fragment key={entry.id}>
                  {alertsItem}
                  {renderNavItem(entry.id)}
                </React.Fragment>
              );
            }
            return renderNavItem(entry.id);
          }

          const GroupIcon = entry.icon;
          const isOpenGroup = collapsed ? true : !!openGroups[entry.id];
          const hasActiveChild = entry.items.includes(activePage);

          return (
            <div
              key={entry.id}
              className={`sidebar-group${isOpenGroup ? " is-open" : ""}${hasActiveChild ? " has-active" : ""}`}
            >
              <button
                type="button"
                className="sidebar-group-toggle"
                onClick={() => !collapsed && toggleGroup(entry.id)}
                aria-expanded={isOpenGroup}
                title={entry.label}
              >
                {GroupIcon && <GroupIcon className="nav-item-icon" />}
                <span className="nav-item-label sidebar-group-label">{entry.label}</span>
                {!collapsed && (
                  <FaChevronDown
                    className={`sidebar-group-chevron${isOpenGroup ? " is-open" : ""}`}
                    size={11}
                  />
                )}
              </button>
              {isOpenGroup && (
                <div className="sidebar-group-items" role="group" aria-label={entry.label}>
                  {entry.items.map((itemId) => renderNavItem(itemId))}
                </div>
              )}
            </div>
          );
        })}
        {hasSupportItem ? null : alertsItem}
      </nav>

      <div className="sidebar-footer" ref={profileRef}>
        <div className="sidebar-nav-divider sidebar-footer-divider" role="separator" />

        {profileMenuOpen ? (
          <div className="sidebar-profile-menu" role="menu" aria-label="Account menu">
            <button
              type="button"
              className="sidebar-profile-menu-item"
              role="menuitem"
              onClick={() => runProfileAction(onMyDetails)}
            >
              <FaUser className="sidebar-profile-menu-icon" aria-hidden />
              <span>My Details</span>
            </button>
            <button
              type="button"
              className="sidebar-profile-menu-item"
              role="menuitem"
              onClick={() => runProfileAction(onChangePassword)}
            >
              <FaKey className="sidebar-profile-menu-icon" aria-hidden />
              <span>Change Password</span>
            </button>
            {isAdminUser ? (
              <button
                type="button"
                className="sidebar-profile-menu-item"
                role="menuitem"
                onClick={() => runProfileAction(onPermissions)}
              >
                <FaLock className="sidebar-profile-menu-icon" aria-hidden />
                <span>Permissions</span>
              </button>
            ) : null}
            <button
              type="button"
              className="sidebar-profile-menu-item"
              role="menuitem"
              onClick={() => runProfileAction(onSettings)}
            >
              <FaCog className="sidebar-profile-menu-icon" aria-hidden />
              <span>Settings</span>
            </button>
            <div className="sidebar-profile-menu-divider" role="separator" />
            <button
              type="button"
              className="sidebar-profile-menu-item sidebar-profile-menu-item--danger"
              role="menuitem"
              onClick={() => runProfileAction(handleSignOut)}
            >
              <FaSignOutAlt className="sidebar-profile-menu-icon" aria-hidden />
              <span>Sign out</span>
            </button>
          </div>
        ) : null}

        <button
          type="button"
          className={`sidebar-account-trigger${profileMenuOpen || profileMenuActive ? " is-active" : ""}`}
          onClick={() => setProfileMenuOpen((v) => !v)}
          aria-expanded={profileMenuOpen}
          aria-haspopup="menu"
          title={userName}
        >
          <span className="sidebar-account-avatar" aria-hidden>
            {userImageUrl && !imageError ? (
              <img src={userImageUrl} alt="" onError={() => setImageError(true)} />
            ) : (
              userInitial
            )}
          </span>
          <span className="sidebar-account-details">
            <span className="sidebar-account-name">{userName}</span>
            <span className="sidebar-account-email">{userEmail || "—"}</span>
          </span>
          <FaChevronUp
            className={`sidebar-account-caret${profileMenuOpen ? " is-open" : ""}`}
            aria-hidden
          />
        </button>
      </div>
    </div>
  );
}

export default Sidebar;
